/**
 * VideoUrlService - Secure preview URL fetching
 *
 * Fetches short-lived signed preview URLs from the backend signing server
 * instead of constructing CDN URLs directly in the browser.
 *
 * In production: backend returns CacheFly signed URLs (15-min TTL)
 * In local dev:  backend returns direct CDN URLs (no signing secret needed)
 *
 * Backend: chillfree-preview-server (GET /api/preview/:videoId)
 */

import { clientEnv } from '../buildTimeEnv';

const PREVIEW_API_BASE =
  clientEnv.REACT_APP_API_BASE_URL || 'http://localhost:3001';


// Fallback: build the direct CDN URL client-side when signing server is unavailable
function _buildDirectUrl(videoId) {
  const base = window.CONTENT_BASE_URL || 'https://chillfreedspmedia.cachefly.net';
  const company = window.COMPANY_ID || '6419d68d33c59c85e30cdc62';
  return `${base}/files/company/${company}/assets/videos/${videoId}/vod/${videoId}.m3u8`;
}

// In-memory cache: videoId → { url, expires }
// Avoids hammering the signing server on every hover
const _urlCache = new Map();

/**
 * Fetch a (signed) preview URL from the backend.
 * Cached for the duration of the token TTL.
 *
 * @param {string} videoId
 * @returns {Promise<string|null>}
 */
export const getDirectVideoUrl = async (videoId, type = 'preview') => {
  if (!videoId || typeof videoId !== 'string' || videoId.length < 10) {
    console.warn('VideoUrlService: Invalid video ID:', videoId);
    return null;
  }

  // Return cached URL if still valid (with 60s buffer)
  const cached = _urlCache.get(videoId);
  if (cached) {
    const nowSec = Math.floor(Date.now() / 1000);
    if (!cached.expires || cached.expires - nowSec > 60) {
      return cached.url;
    }
    _urlCache.delete(videoId); // expired
  }

  try {
    const response = await fetch(`${PREVIEW_API_BASE}/api/preview/${videoId}?companyId=${window.COMPANY_ID || '6419d68d33c59c85e30cdc62'}&type=${type}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      console.warn(`VideoUrlService: Server error ${response.status} — falling back to direct URL`);
      return _buildDirectUrl(videoId);
    }

    const { url, expires } = await response.json();

    if (!url) {
      console.warn('VideoUrlService: No URL returned — falling back to direct URL');
      return _buildDirectUrl(videoId);
    }

    _urlCache.set(videoId, { url, expires });
    return url;

  } catch (error) {
    console.warn('VideoUrlService: Signing server unreachable — falling back to direct URL:', error.message);
    return _buildDirectUrl(videoId);
  }
};

/**
 * Extract video ID from episode data
 */
export const getEpisodeVideoId = episode => {
  if (!episode) return null;
  return episode._id || episode.video?._id || episode.video_id || null;
};

/**
 * Extract video ID from single channel data
 */
export const getSingleChannelVideoId = channel => {
  if (!channel) return null;
  return channel.video?._id || channel.video_id || channel._id || null;
};

/**
 * Get signed preview URL for any content type.
 * Returns a Promise<string|null>.
 *
 * @param {Object} content
 * @param {string} contentType - 'single' | 'episode'
 * @returns {Promise<string|null>}
 */
export const getPreviewUrl = async (content, contentType = 'single') => {
  if (!content) return null;

  const videoId =
    contentType === 'episode'
      ? getEpisodeVideoId(content)
      : getSingleChannelVideoId(content);

  if (!videoId) {
    console.warn('VideoUrlService: Could not extract video ID from content:', {
      _id: content._id,
      title: content.title,
      contentType
    });
    return null;
  }

  return getDirectVideoUrl(videoId);
};

/**
 * Validate HLS URL accessibility (unchanged)
 */
export const validateHlsUrl = async hlsUrl => {
  if (!hlsUrl) return false;
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const response = await fetch(hlsUrl, { method: 'HEAD', signal: controller.signal });
    clearTimeout(timeoutId);
    return response.ok;
  } catch {
    return false;
  }
};

export default {
  getDirectVideoUrl,
  getEpisodeVideoId,
  getSingleChannelVideoId,
  getPreviewUrl,
  validateHlsUrl
};
